import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { throttle } from 'lodash';
import useLocalStorage from 'hooks/useLocalStorage';
import { createPath, ROUTE } from 'utils/Routing';
import FetchData from 'utils/FetchData';
import { ILogin } from 'utils/Interfaces';
import Modal from 'components/Modal/Modal';
import './Login.css';

const Login = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [userData, setUserData] = useState<ILogin | null>(null);
  const [validData, setValidData] = useState(false);
  const [error, setError] = useState({ error: false, body: '' });
  const [, setValue] = useLocalStorage('AUTH');

  const inputName = useRef<HTMLInputElement>(null!);
  const inputPassword = useRef<HTMLInputElement>(null!);
  const inputNameError = useRef<HTMLParagraphElement>(null!);
  const inputPasswordError = useRef<HTMLParagraphElement>(null!);

  const handleSubmit = useCallback((event: any) => {
    event.preventDefault();
    let inputChecks: number = 0;

    if (!inputName.current.value) {
      inputName.current.classList.add('error');
      inputNameError.current.innerText = 'The username field is required.';
      inputChecks += 1;
    } else {
      if (inputName.current.classList.contains('error')) {
        inputName.current.classList.remove('error');
      }
      inputName.current.classList.add('success');
      inputNameError.current.innerText = '';
    }

    if (!inputPassword.current.value) {
      inputPassword.current.classList.add('error');
      inputPasswordError.current.innerText = 'The password field is required.';
      inputChecks += 1;
    } else {
      if (inputPassword.current.classList.contains('error')) {
        inputPassword.current.classList.remove('error');
      }
      inputPassword.current.classList.add('success');
      inputPasswordError.current.innerText = '';
    }

    if (inputChecks > 0) {
      return;
    }

    setValidData(true);
  }, []);

  const delayedHandleSubmit = throttle((e) => handleSubmit(e), 2000);

  const checkLogin = async () => {
    const { response, hasError } = await FetchData({
      method: 'post',
      url: '/auth/local/',
      data: JSON.stringify({ identifier: inputName.current.value, password: inputPassword.current.value }),
    });

    if (!hasError.error) {
      setUserData(response);
      return;
    }

    setValidData(false);
    if (inputName.current.classList.contains('success') || inputPassword.current.classList.contains('success')) {
      inputName.current.classList.remove('success');
      inputPassword.current.classList.remove('success');
      inputName.current.classList.add('error');
      inputPassword.current.classList.add('error');
    }
    setError(hasError);
  };

  useEffect(() => {
    const login = () => {
      if (validData) {
        checkLogin();
        setValue(userData);
      }
    };
    login();
  }, [userData, validData, setValue]);

  return (
    <>
      {userData?.jwt && (
        <Route>
          <Redirect to={createPath({ path: ROUTE.HOME })} />
        </Route>
      )}

      {error.error && (
        <Modal
          open={isOpen}
          info={error.body}
          type='error'
          onClose={() => {
            setError({ error: false, body: '' });
            setIsOpen(true);
          }}
        />
      )}

      <form autoComplete='off' noValidate className='form' id='form'>
        <h1 className='login__title'>Login</h1>
        <div className='g1'>
          <label className='label' htmlFor='name'>
            <span>Username</span>
            <input ref={inputName} name='name' id='name' type='text' className='input' placeholder='John' tabIndex={0} />
          </label>
          <p ref={inputNameError} id='p-name' className='feedback error' />
        </div>
        <div className='g1'>
          <label className='label' htmlFor='password'>
            <span>Password</span>
            <input
              ref={inputPassword}
              name='password'
              id='password'
              type='password'
              className='input'
              placeholder='Enter your password'
              tabIndex={0}
            />
          </label>
          <p ref={inputPasswordError} id='p-password' className='feedback error' />
        </div>
        <div className='g2'>
          <button className='action__button' type='button' form='form' onClick={delayedHandleSubmit}>
            <p className='button-text'>Login</p>
            <i className='bx bx-check' />
          </button>
        </div>
      </form>
    </>
  );
};

export default Login;
